import { t } from "i18next";
import {
  array,
  boolean,
  mixed,
  number,
  object,
  ObjectSchema,
  string,
} from "yup";
import { v4 as uuidv4 } from "uuid";
import { Schemas } from "../../../types";
import isEqual from "lodash/isEqual";

export const UNTITLED_QUESTION = "Untitled question";

export type DataType = Schemas["QuestionnaireQuestionTypeEnum"];
export type CustomQuestionnaireTemplateInput = Required<
  Schemas["CustomQuestionnaireTemplateInput"]
>;

export type FieldTypeKey =
  | "text_field"
  | "multi_line_text"
  | "yes_no"
  | "confirmed_not_confirmed"
  | "yes_no_na"
  | "yes_no_na_none";

type FieldTypeConfig = {
  data_type: DataType;
  selectable_options: Record<string, unknown>[];
  display_options?: Schemas["DisplayOptions"];
};

export const FIELD_TYPE_KEYS = [
  "text_field",
  "multi_line_text",
  "yes_no",
  "confirmed_not_confirmed",
  "yes_no_na",
  "yes_no_na_none",
] as const;

export const FIELD_TYPE_OPTIONS: {
  key: FieldTypeKey;
  label: string;
}[] = [
  { key: "text_field", label: t("Text field") },
  { key: "multi_line_text", label: t("Multi-line text") },
  { key: "yes_no", label: t("List: Yes / No") },
  {
    key: "confirmed_not_confirmed",
    label: t("List: Confirmed / Not confirmed"),
  },
  { key: "yes_no_na", label: t("List: Yes / No / NA") },
  { key: "yes_no_na_none", label: t("List: Yes / No / NA / None") },
];

export const FIELD_TYPE_CONFIGS: Record<FieldTypeKey, FieldTypeConfig> = {
  text_field: {
    data_type: "str",
    selectable_options: [],
    display_options: {},
  },
  multi_line_text: {
    data_type: "str",
    selectable_options: [],
    display_options: { multiline: true },
  },
  yes_no: {
    data_type: "bool",
    selectable_options: [],
    display_options: {},
  },
  confirmed_not_confirmed: {
    data_type: "str",
    selectable_options: [
      { value: "confirmed", label: "Confirmed" },
      { value: "not_confirmed", label: "Not confirmed" },
    ],
    display_options: { widget: "radio" },
  },
  yes_no_na: {
    data_type: "str",
    selectable_options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "na", label: "N/A" },
    ],
    display_options: { widget: "radio" },
  },
  yes_no_na_none: {
    data_type: "str",
    selectable_options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "na", label: "N/A" },
      { value: "none", label: "None" },
    ],
    display_options: { widget: "radio" },
  },
};

export const getFieldTypeKeyFromConfig = (
  config: FieldTypeConfig
): FieldTypeKey | undefined => {
  return (
    Object.entries(FIELD_TYPE_CONFIGS) as [FieldTypeKey, FieldTypeConfig][]
  ).find(([, value]) => isEqual(value, config))?.[0];
};

export const createEmptyQuestion = (
  display_priority: number
): Schemas["CustomQuestionnaireQuestionInput"] => ({
  id: uuidv4(),
  external_name: "",
  description: undefined,
  data_type: "str",
  is_optional: false,
  display_priority,
  display_options: {},
  selectable_options: [],
  display_conditions: [],
});

export const getConditionValueOptions = (
  parent: Schemas["CustomQuestionnaireQuestionInput"],
  t: (key: string) => string
): { value: string; label: string }[] => {
  if (parent.data_type === "bool") {
    return [
      { value: "true", label: t("Yes") },
      { value: "false", label: t("No") },
    ];
  }

  const selectableOptions = parent.selectable_options ?? [];
  if (selectableOptions.length > 0) {
    return selectableOptions.map((opt) => ({
      value: String((opt as { value: string }).value),
      label: String((opt as { label: string }).label),
    }));
  }

  return [];
};

export const getConditionalQuestions = (
  questions: Schemas["CustomQuestionnaireQuestionInput"][],
  currentQuestion: Schemas["CustomQuestionnaireQuestionInput"]
): Schemas["CustomQuestionnaireQuestionInput"][] =>
  questions.filter(
    (question) =>
      question.id !== currentQuestion.id &&
      (question.data_type === "bool" ||
        (question.selectable_options && question.selectable_options.length > 0))
  );

export const questionValidationSchema: ObjectSchema<
  Schemas["CustomQuestionnaireQuestionInput"]
> = object().shape({
  id: string().optional(),
  external_name: string()
    .trim()
    .min(1, "Question text cannot be empty")
    .required(t("Field label is a required field.")),
  description: string().default(""),
  data_type: mixed<DataType>()
    .oneOf(["str", "int", "float", "bool", "file_upload"])
    .required(),
  is_optional: boolean().required(),
  display_priority: number().min(1),
  display_options: mixed().default({}),
  selectable_options: array().default([]),
  display_conditions: array()
    .of(
      object().shape({
        parent_question_id: string().required(),
        condition_value: string().required(),
      })
    )
    .default([]),
});

export const validationSchema: ObjectSchema<CustomQuestionnaireTemplateInput> =
  object().shape({
    name: string().trim().min(1, "Name cannot be empty").required(),
    questions: array()
      .of(questionValidationSchema)
      .min(1, "At least one question is required")
      .required(),
  });
